/**
 * Onboarding data model and step definitions.
 *
 * Onboarding runs inside the chat surface — the persona asks three
 * chip/input-friendly questions, the user replies, the profile is saved
 * via PATCH /v1/users/me/profile.
 *
 *   Step 1 — name (text input, optional + skippable)
 *   Step 2 — stay dates (date inputs, validated)
 *   Step 3 — party + dog combined (compound mode, 8 chips, single tap)
 *
 * Profile is the persistent personalisation record. Read by the 4pm push,
 * memory page generator, business agents, and any feature that needs to
 * know who the user is. Schema unchanged from earlier batches except for
 * the optional `name` field added in v7 and the removal of `allergies`
 * on 3 Jun 2026 (allergen descope, API_CONTRACT.md v8). Existing profiles
 * with legacy `allergies` keys parse fine — Zod's default `.strip()` drops
 * the field.
 */
import { z } from 'zod';

// Allergen options removed 3 Jun 2026 — TownInsider does not filter
// food recommendations on allergens. UK venues are legally responsible
// for allergen info (Natasha's Law, FIR 2014); inserting TownInsider
// into the safety loop creates liability without adding safety. The
// existing phone handoff covers it — Morwenna surfaces the venue's
// phone, the guest calls, the venue does what it's legally bound to.
// See API_CONTRACT.md v8 for the full rationale.

// Retained as a documented constant for any future use (free-text fallback,
// business agent context). Not used by ONBOARDING_STEPS — the onboarding
// flow uses PARTY_WITH_DOG_OPTIONS for a combined single-tap step.
export const PARTY_OPTIONS = [
  { value: 'solo', label: 'Just me' },
  { value: 'couple', label: 'Couple' },
  { value: 'family', label: 'Family' },
  { value: 'group', label: 'Group' },
] as const;

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Name field constraints — kept in sync with the server-side
 * `profileSchema` in `towninsider-api/src/lib/profile.ts` so the client
 * surfaces the same rejection at the same boundary. Defence in depth on
 * the field that feeds the LLM system prompt as user-controlled text.
 *
 * Regex: Unicode letters and combining marks (\p{L}\p{M}) plus spaces,
 * apostrophes, hyphens, and periods. Accepts accented and non-Latin
 * names (O'Connor, Niamh, Søren, 山田); rejects control characters,
 * brackets, colons, slashes, backticks, braces, and other characters
 * that carry prompt-injection signal.
 *
 * Length capped at 50 — well above any real name, well below anything
 * that would meaningfully inflate the system prompt.
 */
const NAME_REGEX = /^[\p{L}\p{M}\s'\-.]+$/u;
const NAME_MAX_LENGTH = 50;

export const profileSchema = z
  .object({
    stayStart: z.string().regex(ISO_DATE),
    stayEnd: z.string().regex(ISO_DATE),
    party: z.enum(['solo', 'couple', 'family', 'group']),
    hasDog: z.boolean(),
    /**
     * Preferred name for the persona. Optional —
     * guests can skip during onboarding, in which case the field
     * is omitted (not sent as null). Validated against the same
     * charset/length as the API write boundary; values that fail
     * are rejected client-side before PATCH so the user can fix
     * them in the field rather than discover a 422 after submit.
     */
    name: z
      .string()
      .trim()
      .min(1)
      .max(NAME_MAX_LENGTH)
      .regex(NAME_REGEX, 'Name contains unsupported characters')
      .optional(),
  })
  .strict();

export type Profile = z.infer<typeof profileSchema>;
export type Party = Profile['party'];

/** Onboarding step ids, in flow order. */
export type OnboardingStepId =
  | 'name'
  | 'stay-dates'
  | 'party-with-dog';

/** Carried atomically by a single chip in the compound step. */
export interface PartyWithDogSelection {
  readonly party: Party;
  readonly hasDog: boolean;
}

/** Emitted by the date-input step when the user taps Done. */
export interface StayDatesSelection {
  readonly stayStart: string;
  readonly stayEnd: string;
}

/**
 * Emitted by the text-input step when the user submits or skips.
 *
 * `name: undefined` represents both an explicit Skip tap and an
 * OK tap on an empty/whitespace-only field — same outcome either
 * way (no name persisted, persona addresses guest neutrally).
 */
export interface NameSelection {
  readonly name: string | undefined;
}

/** Standard string-valued chip option — retained as a re-usable shape
 *  for any future multi-select step. No current step uses it (the
 *  'multi' variant was removed in the allergen descope, 3 Jun 2026). */
export interface QuickReplyOption {
  readonly value: string;
  readonly label: string;
}

/** Chip option carrying a structured value (used by the compound step). */
export interface QuickReplyCompoundOption {
  readonly value: PartyWithDogSelection;
  readonly label: string;
}

/**
 * Discriminated union of onboarding steps. The mode field narrows the
 * options shape and the selection type the chip UI will emit.
 *
 * The 'text' mode (added v7) is consumed by `OnboardingTextStep` and
 * carries layout-affecting metadata (label, placeholder, maxLength)
 * inline so the component is a pure function of the step config.
 *
 * The 'multi' mode (allergies) was removed 3 Jun 2026 in the allergen
 * descope (API_CONTRACT.md v8). If a future onboarding step needs
 * multi-select chips, reintroduce the variant and add a corresponding
 * QuickReplies branch.
 */
export type OnboardingStep =
  | {
      readonly id: 'name';
      readonly mode: 'text';
      readonly label: string;
      readonly placeholder: string;
      readonly maxLength: number;
    }
  | {
      readonly id: 'stay-dates';
      readonly mode: 'dates';
    }
  | {
      readonly id: 'party-with-dog';
      readonly mode: 'compound';
      readonly options: ReadonlyArray<QuickReplyCompoundOption>;
    };

/**
 * Selection emitted by a step on submit:
 *   text step     → NameSelection (single object, OK or Skip tap)
 *   dates step    → StayDatesSelection (single object, Done tap)
 *   compound step → PartyWithDogSelection (single object, single chip tap)
 */
export type OnboardingSelection =
  | NameSelection
  | StayDatesSelection
  | PartyWithDogSelection;

/** All eight party + dog combinations as a fixed grid. */
export const PARTY_WITH_DOG_OPTIONS: ReadonlyArray<QuickReplyCompoundOption> = [
  { value: { party: 'solo', hasDog: false }, label: 'Just me' },
  { value: { party: 'solo', hasDog: true }, label: 'Just me + dog' },
  { value: { party: 'couple', hasDog: false }, label: 'Couple' },
  { value: { party: 'couple', hasDog: true }, label: 'Couple + dog' },
  { value: { party: 'family', hasDog: false }, label: 'Family' },
  { value: { party: 'family', hasDog: true }, label: 'Family + dog' },
  { value: { party: 'group', hasDog: false }, label: 'Group' },
  { value: { party: 'group', hasDog: true }, label: 'Group + dog' },
];

/**
 * Step ordering: name first. The persona's welcomeIntro is a
 * self-introduction ("I'm Morwenna, your Padstow Insider..."), and
 * asking the guest's name next is the natural conversational beat
 * before getting into logistics. Skipping is supported and natural.
 *
 * Allergies step removed 3 Jun 2026 — see header note on ALLERGEN_OPTIONS.
 */
export const ONBOARDING_STEPS: ReadonlyArray<OnboardingStep> = [
  {
    id: 'name',
    mode: 'text',
    label: 'Your name',
    placeholder: 'First name',
    maxLength: NAME_MAX_LENGTH,
  },
  { id: 'stay-dates', mode: 'dates' },
  {
    id: 'party-with-dog',
    mode: 'compound',
    options: PARTY_WITH_DOG_OPTIONS,
  },
];

/** Format a Date as YYYY-MM-DD using LOCAL components (not UTC). */
export function isoDateLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const MAX_STAY_DAYS = 28;

/**
 * Validate a stay-dates pair. Returns null if valid, otherwise a
 * user-facing error message.
 *
 * Rules:
 *   - Both must match ISO YYYY-MM-DD.
 *   - Arrival cannot be in the past (compared as local-date strings).
 *   - Departure must be strictly after arrival (≥ 1 night).
 *   - Stay length capped at MAX_STAY_DAYS (4 weeks) — sensible holiday
 *     upper bound; a longer stay is almost certainly user error.
 */
export function validateStayDates(
  stayStart: string,
  stayEnd: string,
): string | null {
  if (!ISO_DATE.test(stayStart)) return 'Pick an arrival date.';
  if (!ISO_DATE.test(stayEnd)) return 'Pick a departure date.';

  const today = isoDateLocal(new Date());
  if (stayStart < today) return "Arrival can't be in the past.";
  if (stayEnd <= stayStart) return 'Departure must be after arrival.';

  const startMs = new Date(`${stayStart}T00:00:00`).getTime();
  const endMs = new Date(`${stayEnd}T00:00:00`).getTime();
  const days = Math.round((endMs - startMs) / 86_400_000);
  if (days > MAX_STAY_DAYS) {
    return `Stays of more than ${MAX_STAY_DAYS} days aren't supported yet — message your Insider.`;
  }
  return null;
}

/**
 * Validate a name input. Returns null if valid (or empty — empty is
 * treated as "skip" by the step component), otherwise a user-facing
 * error message. Matches the server-side `profileSchema` regex
 * exactly so the constraint surfaces at the same boundary.
 */
export function validateName(value: string): string | null {
  const trimmed = value.trim();
  if (trimmed.length === 0) return null;
  if (trimmed.length > NAME_MAX_LENGTH) {
    return `Names must be ${NAME_MAX_LENGTH} characters or fewer.`;
  }
  if (!NAME_REGEX.test(trimmed)) {
    return 'Names can include letters, spaces, hyphens, and apostrophes.';
  }
  return null;
}

/** "2026-05-13" → "13 May" (UK locale, short month). */
export function formatStayDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
  });
}

/**
 * "2026-05-07", "2026-05-14" → "7–14 May"
 * "2026-05-30", "2026-06-05" → "30 May – 5 Jun"
 *
 * Compact form when both dates fall in the same month/year, fuller form
 * otherwise. Year is omitted — stays span days/weeks, not years.
 */
export function formatStayRange(stayStart: string, stayEnd: string): string {
  const startD = new Date(`${stayStart}T00:00:00`);
  const endD = new Date(`${stayEnd}T00:00:00`);
  const sameMonth =
    startD.getMonth() === endD.getMonth() &&
    startD.getFullYear() === endD.getFullYear();
  if (sameMonth) {
    const startDay = startD.toLocaleDateString('en-GB', { day: 'numeric' });
    const endDay = endD.toLocaleDateString('en-GB', { day: 'numeric' });
    const month = startD.toLocaleDateString('en-GB', { month: 'short' });
    return `${startDay}\u2013${endDay} ${month}`;
  }
  return `${formatStayDate(stayStart)} \u2013 ${formatStayDate(stayEnd)}`;
}

/**
 * Build a complete Profile from onboarding answers. Stay dates,
 * party, and hasDog are required; name is optional and only included
 * in the resulting profile when provided.
 *
 * Conditional spread (lesson 28) preserves exactOptionalPropertyTypes
 * semantics — "name absent" is distinguishable from "name: undefined"
 * in the resulting Profile, and the strict Zod schema rejects neither.
 */
export function buildProfile(answers: {
  stayStart: string;
  stayEnd: string;
  party: Party;
  hasDog: boolean;
  name?: string;
}): Profile {
  return {
    stayStart: answers.stayStart,
    stayEnd: answers.stayEnd,
    party: answers.party,
    hasDog: answers.hasDog,
    ...(answers.name !== undefined ? { name: answers.name } : {}),
  };
}

/** Type guard — narrows to the stay-dates selection shape. */
export function isStayDatesSelection(
  selection: OnboardingSelection,
): selection is StayDatesSelection {
  return (
    typeof selection === 'object' &&
    !Array.isArray(selection) &&
    'stayStart' in selection &&
    'stayEnd' in selection
  );
}

/** Type guard — narrows to the compound (party-with-dog) selection shape. */
export function isPartyWithDogSelection(
  selection: OnboardingSelection,
): selection is PartyWithDogSelection {
  return (
    typeof selection === 'object' &&
    !Array.isArray(selection) &&
    'party' in selection &&
    'hasDog' in selection
  );
}

/**
 * Type guard — narrows to the name (text-step) selection shape.
 * Disambiguated from StayDatesSelection (`stayStart`/`stayEnd`) and
 * PartyWithDogSelection (`party`/`hasDog`) by their unique keys; no
 * NameSelection ever shares a key with the others.
 */
export function isNameSelection(
  selection: OnboardingSelection,
): selection is NameSelection {
  return (
    typeof selection === 'object' &&
    selection !== null &&
    !Array.isArray(selection) &&
    'name' in selection &&
    !('stayStart' in selection) &&
    !('party' in selection)
  );
}

/**
 * Format the user's selection for display in their reply bubble.
 *   text     — the typed name, or 'Skip' if the user skipped
 *   dates    — formatted range (e.g. "7–14 May")
 *   compound — the matching chip's label (e.g. "Couple + dog")
 */
export function formatSelection(
  step: OnboardingStep,
  selection: OnboardingSelection,
): string {
  switch (step.mode) {
    case 'text': {
      if (!isNameSelection(selection)) return '';
      return selection.name ?? 'Skip';
    }
    case 'dates': {
      if (!isStayDatesSelection(selection)) return '';
      return formatStayRange(selection.stayStart, selection.stayEnd);
    }
    case 'compound': {
      if (!isPartyWithDogSelection(selection)) return '';
      const opt = step.options.find(
        (o) =>
          o.value.party === selection.party &&
          o.value.hasDog === selection.hasDog,
      );
      return opt?.label ?? '';
    }
  }
}

/** Party label for ack messages: "just you" / "a couple" / "a family" / "a group". */
export function describeParty(party: Party): string {
  switch (party) {
    case 'solo':
      return 'just you';
    case 'couple':
      return 'a couple';
    case 'family':
      return 'a family';
    case 'group':
      return 'a group';
  }
}

/** Capitalise the first character only. Safe on empty strings. */
export function capitaliseFirst(s: string): string {
  return s.length === 0 ? s : s.charAt(0).toUpperCase() + s.slice(1);
}
